// API service functions for weekly plan

const API_URL = process.env.REACT_APP_API_URL;

export const weeklyPlanAPI = {
  // Fetch weekly plan recipes - now supports plan_type parameter
  fetchPlan: (planType = null) => {
    const url = planType 
      ? `${API_URL}/api/weekly_plan_recipes?plan_type=${planType}`
      : `${API_URL}/api/weekly_plan_recipes`;
    return fetch(url, {
      credentials: "include"
    }).then(res => res.json());
  },

  // Add recipe to weekly plan - now supports plan_type
  addRecipe: (data, planType = null) => {
    const requestData = planType ? { ...data, plan_type: planType } : data;
    return fetch(`${API_URL}/api/add-weekly-plan-recipe`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(requestData)
    });
  },

  // Add meal to weekly plan (meals are added as individual recipes)
  addMeal: (data) => 
    fetch(`${API_URL}/api/add-weekly-plan-recipe`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data)
    }),

  // Update recipe in weekly plan
  updateRecipe: (data) => 
    fetch(`${API_URL}/api/update-weekly-plan-recipe`, {
      method: "PUT",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data)
    }),

  // Delete recipe from weekly plan
  deleteRecipe: (id) => 
    fetch(`${API_URL}/api/delete-weekly-plan-recipe/${id}`, {
      method: "DELETE",
      credentials: "include"
    }),

  // Remove recipe from plan (with shopping list cleanup)
  removeRecipeFromPlan: (data) => 
    fetch(`${API_URL}/api/remove_recipe_from_plan/`, {
      method: "DELETE",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data)
    }),

  // Fetch recipe details
  fetchRecipeDetails: (recipeId) => 
    fetch(`${API_URL}/api/recipes/${recipeId}`, {
      credentials: "include"
    }).then(res => res.json()),

  // Fetch recipe ingredients
  fetchRecipeIngredients: (recipeId) => 
    fetch(`${API_URL}/api/recipe/ingredients/${recipeId}`, {
      credentials: "include"
    }).then(res => res.json()),

  // Fetch meal recipes
  fetchMealRecipes: (mealId) => 
    fetch(`${API_URL}/api/meal-recipes/${mealId}`, {
      credentials: "include"
    }).then(res => res.json()),

  // Add shopping item from weekly plan
  addShoppingItem: (data) => 
    fetch(`${API_URL}/add-shopping-item-from-weekly-plan`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data)
    }),

  // Fetch all recipes
  fetchAllRecipes: () => 
    fetch(`${API_URL}/api/recipes`, {
      credentials: "include"
    }).then(res => {
      if (res.ok) {
        return res.json();
      } else {
        throw new Error("Error retrieving recipes from server");
      }
    }),

  // Fetch user meals
  fetchUserMeals: () => 
    fetch(`${API_URL}/api/user_meals`, {
      credentials: "include"
    }).then(res => {
      if (res.ok) {
        return res.json();
      } else {
        throw new Error("Error retrieving meals from server");
      }
    }),

  // Fetch user settings
  fetchUserSettings: () => 
    fetch(`${API_URL}/api/getUser`, {
      method: "GET",
      credentials: "include",
      headers: { "Content-Type": "application/json" }
    }).then(res => res.json()),

  // Update user settings
  updateUserSettings: (setting, value) => 
    fetch(`${API_URL}/api/updateUser/${setting}`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ [setting]: value })
    }),

  // Check if user is logged in
  checkAuth: () => 
    fetch(`${API_URL}/api/isLoggedIn`, {
      credentials: "include"
    }).then(res => res.json()),

  // Delete weekly plan entry without affecting shopping list
  deleteWeeklyPlanOnly: (rowId) => 
    fetch(`${API_URL}/api/weekly-plan-direct-delete`, {
      method: "DELETE",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ rowId })
    }),

  // Update shopping list items' day/meal when moving a recipe
  updateShoppingListDayMeal: (data) => 
    fetch(`${API_URL}/api/update-shopping-list-day-meal`, {
      method: "PUT",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data)
    }),

  // NEW: Transition from next week to current week
  transitionToNextWeek: () => 
    fetch(`${API_URL}/api/transition-to-next-week`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" }
    }).then(res => {
      if (res.ok) {
        return res.json();
      } else {
        return res.json().then(err => Promise.reject(err));
      }
    }),

  // NEW: Get summary of current and next week plans
  fetchPlanSummary: () => 
    fetch(`${API_URL}/api/weekly-plan-summary`, {
      credentials: "include"
    }).then(res => {
      if (res.ok) {
        return res.json();
      } else {
        return res.json().then(err => Promise.reject(err));
      }
    }),

  // NEW: Get user settings including feature flags
  getUserSettings: () => 
    fetch(`${API_URL}/api/getUser`, {
      credentials: "include"
    }).then(res => {
      if (res.ok) {
        return res.json();
      } else {
        return res.json().then(err => Promise.reject(err));
      }
    }),

  // NEW: Update next week planning feature flag
  updateNextWeekPlanning: (enabled) => 
    fetch(`${API_URL}/api/updateUser/next_week_planning`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ enabled })
    }),

  // Fetch weekly plan prep items
  fetchWeeklyPrepItems: () => 
    fetch(`${API_URL}/api/weekly-plan-prep-items`, {
      credentials: "include"
    }).then(res => {
      if (res.ok) {
        return res.json();
      } else {
        return res.json().then(err => Promise.reject(err));
      }
    })
};